import useTranslation from 'next-translate/useTranslation';
import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { NAVIGATION_KEYS } from 'src/interfaces/enums';
import { DeleteIcon, EditIcon, MoreOptionsDotIcon } from '../../icons';
import ContextMenu from '../ContextMenu';
import ContextMenuItem from '../ContextMenuItem';
import IconButton from '../IconButton';
import { FIXED_POSITION_INTERNAL_MENU, TIME_WAIT_CONFIRM_DELETION } from './constants';
import styles from './styles.module.scss';
import { useListNavigationByKeyboard } from './hooks/useListNavigationByKeyboard';
export interface INotesMenuProps {
  disabled?: boolean;
  className?: string;
  onEdit: () => void;
  onDelete: () => void;
}

const NotesMenu = ({ disabled = false, className, onDelete, onEdit }: INotesMenuProps) => {
  const { registerHTMLElement } = useListNavigationByKeyboard();
  const { t } = useTranslation('common');
  const contextMenuPosition = {
    clientX: FIXED_POSITION_INTERNAL_MENU.left,
    clientY: FIXED_POSITION_INTERNAL_MENU.top,
  };
  const [contextMenuVisible, setContextMenuVisible] = useState(false);
  const [openConfirmDeletion, setOpenConfirmDeletion] = useState(false);

  useEffect(() => {
    if (!openConfirmDeletion) {
      return;
    }

    const time = setTimeout(() => {
      setOpenConfirmDeletion(false);
    }, TIME_WAIT_CONFIRM_DELETION);

    return () => {
      clearTimeout(time);
    };
  }, [openConfirmDeletion]);

  return (
    <div className={classNames(styles.container, className)}>
      <div
        data-testid='container-context-menu'
        className={classNames(styles.contentButtonMenu, { [styles.contextMenuVisible]: contextMenuVisible })}
      >
        <IconButton
          className={styles.buttonMenu}
          disabled={disabled}
          onClick={() => {
            setOpenConfirmDeletion(false);
            setContextMenuVisible(true);
          }}
          aria-haspopup
          icon={<MoreOptionsDotIcon />}
        />
      </div>
      <ContextMenu
        className={classNames(styles.contextMenu, { [styles.contextMenuVisible]: contextMenuVisible })}
        visible={contextMenuVisible}
        position={contextMenuPosition}
        onHide={() => {
          setContextMenuVisible(false);
        }}
        onKeyDown={(e) => {
          if (e.key === NAVIGATION_KEYS.ESCAPE) {
            e.stopPropagation();
            setContextMenuVisible(false);
          }
        }}
      >
        {contextMenuVisible && (
          <>
            <ContextMenuItem
              {...registerHTMLElement({ startWithFocus: contextMenuVisible })}
              className={styles.menuItem}
              icon={<EditIcon />}
              text={t('notes.edit')}
              onClick={() => {
                setContextMenuVisible(false);
                onEdit();
              }}
            />
            {!openConfirmDeletion ? (
              <ContextMenuItem
                {...registerHTMLElement({})}
                className={styles.menuItem}
                icon={<DeleteIcon />}
                text={t('notes.delete')}
                onClick={() => {
                  setOpenConfirmDeletion(true);
                }}
              />
            ) : (
              <ContextMenuItem
                {...registerHTMLElement({ startWithFocus: openConfirmDeletion })}
                className={classNames(styles.menuItem, styles.confirm)}
                icon={<DeleteIcon />}
                text={t('notes.confirm')}
                onClick={() => {
                  setOpenConfirmDeletion(false);
                  setContextMenuVisible(false);
                  onDelete();
                }}
              />
            )}
          </>
        )}
      </ContextMenu>
    </div>
  );
};

export default NotesMenu;
